import type { PermissionOf } from "../../../access/types.ts";
import type { AzureResourceAccess } from "./accesses.ts";

/**
 * Extracts the last part of an Azure role identifier after the last "/"
 * @param roleId The full Azure role identifier (e.g. "Microsoft.Authorization/roleDefinitions/12345678-1234-1234-1234-123456789012")
 * @returns The last part of the role identifier, or empty string if undefined/invalid
 */
export const toRoleShortId = (roleId?: string) => {
  if (!roleId) return "";
  if (roleId.trim() === "") return "";

  // Handle case where there are no slashes
  if (!roleId.includes("/")) return roleId;

  const parts = roleId.split("/");
  const lastPart = parts[parts.length - 1];

  // Handle case where last part is empty (e.g. "role/")
  if (!lastPart) return parts[parts.length - 2] || "";

  return lastPart;
};

/**
 * Extracts the last part of an Azure resource identifier after the last "/"
 * @param resourceId The full Azure resource identifier (e.g. "subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Storage/storageAccounts/{storageAccountName}")
 * @returns The last part of the resource identifier, or empty string if undefined/invalid
 */
export const toResourceName = (
  permission?: PermissionOf<AzureResourceAccess>
) => {
  if (!permission) return "";

  if (permission.resourceType === "subscription") {
    return permission.subscriptionLabel ?? permission.subscription;
  }

  if (!permission?.resource) return "";
  const resourceId = permission.resource;
  if (!resourceId) return "";
  if (resourceId.trim() === "") return "";

  // Handle case where there are no slashes
  if (!resourceId.includes("/")) return resourceId;

  const parts = resourceId.split("/");
  const lastPart = parts[parts.length - 1];

  // Handle case where last part is empty (e.g. "resource/")
  if (!lastPart) return parts[parts.length - 2] || "";

  // Handle case where last part contains query parameters or fragments
  const cleanLastPart = lastPart.split(/[?#]/)[0];

  return cleanLastPart;
};
